import { expectDefined } from '@utils';

/**
 * Unparser
 *
 * A utility class for constructing and formatting a token tree representation of code or text,
 * enabling output in either a compact or pretty printed form.
 *
 * ## Overview
 *
 * The Unparser API is designed to let clients build a flexible stream of tokens and groups that can be rendered
 * in two distinct styles:
 *
 * - **Compact**: Minimizes whitespace and outputs tokens inline.
 * - **Pretty**: Applies line breaks and indentation for improved readability.
 *
 * Unparser uses an internal tree of Node objects to represent tokens, line breaks, and groups. Each group
 * can be configured with formatting options (e.g., whether it should always be compact or support pretty printing
 * with additional line breaks and indentation). Clients can control group behavior using:
 *
 * - `start(delimiter)` – Begin a new group that forces a newline after the delimiter.
 * - `startCompact(delimiter)` – Begin a new group that remains inline (compact).
 * - `startCompactLeft(delimiter)` – Like `startCompact` but with left alignment.
 * - `end(delimiter)` and `endLeft(delimiter)` – End the current group.
 *
 * Other methods include:
 *
 * - `add(token, tokenPretty?)` and `addLeft(token, tokenPretty?)` – Add tokens to the current node list.
 * - `endLine()` – Insert a line break.
 *
 * After building the token tree, call `compact()` to produce the compact string, or `pretty()` for
 * the indented, human-friendly version.
 *
 * ## Implementation Details
 *
 * - The internal structure is based on a tree of Node objects, with each Node representing a token, line break, or group.
 * - A group stack manages the current context for nested groups.
 * - Two recursive routines (`formatCompactNodes` and `formatPrettyNodes`) traverse this tree to generate the final output.
 * - The `expectDefined` utility is used instead of the `!` operator to satisfy strict linting rules.
 *
 * ## Usage Example
 *
 * ```typescript
 * const u = new Unparser('  '); // Two-space indent
 * u.add('a');
 * u.add('=');
 * u.add('b');
 * u.start('{');
 *   u.add('c');
 *   u.addLeft(':');
 *   u.add('d');
 *   u.endLine();
 * u.end('}');
 * console.log(u.compact());
 * console.log(u.pretty());
 * ```
 *
 * ## Note
 *
 * The API intentionally preserves its call sequence to control both compact and pretty outputs. While
 * the underlying implementation manages the formatting via stateful recursion, the client need not worry about
 * the details. Use the provided methods to group and format tokens according to your needs.
 */

interface Node {
  type: 'token' | 'lineBreak' | 'group';
  token?: string;
  tokenPretty?: string;
  left?: boolean;
  newlineAfter?: boolean;
  compact?: boolean;
  children?: Node[];
  endToken?: string;
  endLeft?: boolean;
}

export class Unparser {
  private indentUnit: string;
  private nodes: Node[] = [];
  private groupStack: Node[] = [];

  constructor(indentUnit: string) {
    this.indentUnit = indentUnit;
  }

  // Compute the current node list from the group stack.
  private get currentNodes(): Node[] {
    return this.groupStack.length > 0
      ? expectDefined(this.groupStack[this.groupStack.length - 1].children)
      : this.nodes;
  }

  private pushNode(node: Node): void {
    this.currentNodes.push(node);
  }

  add(token: string, tokenPretty?: string): void {
    this.pushNode({ type: 'token', token, tokenPretty, left: false });
  }

  addLeft(token: string, tokenPretty?: string): void {
    this.pushNode({ type: 'token', token, tokenPretty, left: true });
  }

  endLine(): void {
    this.pushNode({ type: 'lineBreak' });
  }

  // Consolidated helper: options dictate group behavior.
  private startGroup(
    delimiter: string,
    options: { compact: boolean; newlineAfter: boolean; left: boolean }
  ): void {
    const group: Node = {
      type: 'group',
      token: delimiter,
      tokenPretty: delimiter,
      left: options.left,
      newlineAfter: options.newlineAfter,
      compact: options.compact,
      children: []
    };
    this.pushNode(group);
    this.groupStack.push(group);
  }

  start(delimiter: string): void {
    this.startGroup(delimiter, {
      compact: false,
      newlineAfter: true,
      left: false
    });
  }

  startCompact(delimiter: string): void {
    this.startGroup(delimiter, {
      compact: true,
      newlineAfter: false,
      left: false
    });
  }

  startCompactLeft(delimiter: string): void {
    this.startGroup(delimiter, {
      compact: true,
      newlineAfter: false,
      left: true
    });
  }

  private endGroup(delimiter: string, left: boolean): void {
    if (this.groupStack.length === 0) {
      throw new Error('Unbalanced end: no open group to end.');
    }
    const group = expectDefined(this.groupStack.pop());
    group.endToken = delimiter;
    group.endLeft = left;
  }

  end(delimiter: string): void {
    this.endGroup(delimiter, false);
  }

  endLeft(delimiter: string): void {
    this.endGroup(delimiter, true);
  }

  // --- Revised Compact Formatter ---
  private formatCompactNodes(
    nodes: Node[],
    first: boolean
  ): { result: string; first: boolean } {
    let res = '';
    let isFirst = first;

    const append = (text: string, left: boolean) => {
      if (isFirst) {
        res += text;
        isFirst = false;
      } else {
        res += left ? text : ' ' + text;
      }
    };

    for (const node of nodes) {
      if (node.type === 'lineBreak') {
        continue;
      } else if (node.type === 'token') {
        append(expectDefined(node.token), node.left ?? false);
      } else {
        // group
        append(expectDefined(node.token), node.left ?? false);
        const childRes = this.formatCompactNodes(expectDefined(node.children), false);
        res += childRes.result;
        isFirst = childRes.first;
        append(node.endToken ?? '', node.endLeft ?? false);
      }
    }
    return { result: res, first: isFirst };
  }

  compact(): string {
    return this.formatCompactNodes(this.nodes, true).result.trim();
  }

  // --- Revised Pretty Formatter ---
  private formatPrettyNodes(
    nodes: Node[],
    indentLevel: number,
    atLineStart: boolean
  ): { result: string; atLineStart: boolean } {
    let res = '';
    let currentLineStart = atLineStart;

    const append = (text: string, left: boolean) => {
      if (currentLineStart) {
        res += this.indentUnit.repeat(indentLevel) + text;
        currentLineStart = false;
      } else {
        res += left ? text : ' ' + text;
      }
    };

    for (const node of nodes) {
      if (node.type === 'lineBreak') {
        // If the last character is already a `\n`, don't trim it.
        if (!res.endsWith('\n')) {
          res = res.trimEnd();
        }
        res += '\n';
        currentLineStart = true;
      } else if (node.type === 'token') {
        append(node.tokenPretty ?? expectDefined(node.token), node.left ?? false);
      } else {
        // group
        append(node.tokenPretty ?? expectDefined(node.token), node.left ?? false);
        if (node.newlineAfter) {
          res += '\n';
          const childRes = this.formatPrettyNodes(
            expectDefined(node.children),
            indentLevel + 1,
            true
          );
          res += childRes.result;
          currentLineStart = childRes.atLineStart;
          if (currentLineStart) {
            res += this.indentUnit.repeat(indentLevel) + (node.endToken ?? '');
            currentLineStart = false;
          } else {
            res += ' ' + (node.endToken ?? '');
          }
        } else {
          const childRes = this.formatPrettyNodes(
            expectDefined(node.children),
            indentLevel,
            false
          );
          res += childRes.result;
          currentLineStart = false;
          append(node.endToken ?? '', node.endLeft ?? false);
        }
      }
    }
    return { result: res, atLineStart: currentLineStart };
  }

  pretty(): string {
    return this.formatPrettyNodes(this.nodes, 0, true).result.trim();
  }

  get atLineStart(): boolean {
    // We are at BOL if the very last compact character is '\n' **or**
    // we haven’t emitted anything yet.
    if (this.nodes.length === 0) return true;
    const txt = this.compact();
    return txt.length === 0 || txt.endsWith('\n');
  }
}
